// askSmart-ui/src/pages/TOSP/components/CamCategoryPanel.tsx
import React, { useEffect, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import CamCategoryModal from "./CamCategoryModal";
import { EntityOption } from "./SplitMappingFields";
import {
  CamCategory,
  isSplitMappingComplete,
  PERCENT_SPLIT,
  SplitAmong,
  splitEntityLabel,
} from "../types";

interface CamCategoryPanelProps {
  /** The committed categories — what the rest of the app currently sees. */
  values: CamCategory[];
  entityOptions: (splitAmong: SplitAmong | "") => EntityOption[];
  /** May be async — the commit dialog waits on it and shows what it throws. */
  onCommit: (values: CamCategory[]) => void | Promise<void>;
}

// Collapsed, a category is just its name and what you can do with it — the
// split reads better expanded than squeezed into columns.
const GRID_COLUMNS = "1fr 1.2fr";

// CAM Categories — a management grid rather than a plain lookup list, because
// each category carries the split its costs are divided by. Same commit
// workflow as the panels beside it: nothing leaves the panel until committed.
export default function CamCategoryPanel({
  values,
  entityOptions,
  onCommit,
}: CamCategoryPanelProps) {
  const [draft, setDraft] = useState<CamCategory[]>(values);
  const [helpOpen, setHelpOpen] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  /** Why the last commit didn't land, shown inside the confirmation. */
  const [commitError, setCommitError] = useState<string | null>(null);
  // The category being edited, `null` when adding, `undefined` when closed.
  const [editing, setEditing] = useState<CamCategory | null | undefined>(undefined);
  const [removing, setRemoving] = useState<CamCategory | null>(null);
  // Rows opened to show their split, the way SMRT Tasks expands a task.
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  // Reset whenever the committed list changes underneath — including straight
  // after this panel's own commit.
  useEffect(() => {
    setDraft(values);
  }, [values]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(values);

  const save = (category: CamCategory) => {
    setDraft((prev) =>
      prev.some((c) => c.id === category.id)
        ? prev.map((c) => (c.id === category.id ? category : c))
        : [...prev, category],
    );
    setEditing(undefined);
  };

  // Every chosen entity, by name. Kept as one string so the grid cell wraps
  // rather than needing its own list markup.
  const entityLabelsFor = (category: CamCategory): string => {
    const options = entityOptions(category.split.splitAmong);
    const chosen = category.split.entities || [];
    if (chosen.length === 0) return "—";
    return chosen
      .map((value) => options.find((o) => o.value === value)?.label || value)
      .join(", ");
  };

  return (
    <section className="tosp-config-panel">
      <div className="tosp-config-header">
        <h3 className="tosp-config-title">CAM Categories</h3>
        <button
          type="button"
          className="tosp-help-toggle"
          aria-expanded={helpOpen}
          title="What's this?"
          onClick={() => setHelpOpen((open) => !open)}
        >
          ?
        </button>
        <button
          type="button"
          className="tosp-panel-add"
          title="Add a category"
          onClick={() => setEditing(null)}
        >
          +
        </button>
      </div>

      {helpOpen && (
        <p className="tosp-config-note">
          Create and edit Common Area Maintenance (CAM) categories to define cost splits and
          assign to budget line items.
        </p>
      )}

      <div className="tosp-grid-header" style={{ gridTemplateColumns: GRID_COLUMNS }}>
        <div>Category</div>
        <div>Actions</div>
      </div>

      {draft.length === 0 && <div className="tosp-grid-empty">No categories yet.</div>}

      {draft.map((category) => {
        const isExpanded = expandedIds.includes(category.id);
        const { split } = category;
        const percentages = split.percentages || {};
        const showPercentages = split.basedOn === PERCENT_SPLIT;
        return (
          <div key={category.id}>
            <div className="tosp-grid-row" style={{ gridTemplateColumns: GRID_COLUMNS }}>
              {/* "Ignore" is a decision; "No Split" is an omission — flagged
                  so an unfinished category is visible without expanding it. */}
              <div className="tosp-cell-wrap">
                {category.name}
                {split.ignored ? (
                  <span className="tosp-tag">Ignore</span>
                ) : (
                  !isSplitMappingComplete(split) && (
                    <span className="tosp-tag tosp-tag-warn">No Split</span>
                  )
                )}
              </div>
              <div className="tosp-actions">
                <button
                  type="button"
                  className="tosp-btn-icon tosp-btn-view tosp-btn-icon-sm"
                  title={isExpanded ? "Hide split" : "Show split"}
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpanded(category.id)}
                >
                  👁
                </button>
                <button
                  type="button"
                  className="tosp-btn-icon tosp-btn-edit tosp-btn-icon-sm"
                  title="Edit"
                  onClick={() => setEditing(category)}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="tosp-btn-icon tosp-btn-delete tosp-btn-icon-sm"
                  title="Delete"
                  onClick={() => setRemoving(category)}
                >
                  🗑
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="tosp-row-details">
                {split.ignored ? (
                  <div>
                    <strong>Ignore:</strong> excluded from budget calculations.
                  </div>
                ) : (
                  <>
                    <div>
                      <strong>Split Among:</strong> {split.splitAmong || "—"}
                    </div>
                    <div>
                      <strong>{splitEntityLabel(split.splitAmong)}:</strong>{" "}
                      {entityLabelsFor(category)}
                    </div>
                    <div>
                      <strong>Based On:</strong> {split.basedOn || "—"}
                    </div>
                  </>
                )}
                {!split.ignored &&
                  showPercentages &&
                  (split.entities || []).map((entity) => {
                    const label =
                      entityOptions(split.splitAmong).find((o) => o.value === entity)?.label ||
                      entity;
                    return (
                      <div key={entity}>
                        <strong>{label}:</strong>{" "}
                        {percentages[entity] === undefined ? "—" : `${percentages[entity]}%`}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}

      <div className="tosp-config-footer">
        <button
          type="button"
          className="tosp-commit-btn"
          disabled={!isDirty}
          title={isDirty ? undefined : "No changes to commit"}
          onClick={() => setCommitOpen(true)}
        >
          Commit
        </button>
      </div>

      <CamCategoryModal
        isOpen={editing !== undefined}
        category={editing ?? null}
        categories={draft}
        entityOptions={entityOptions}
        onClose={() => setEditing(undefined)}
        onSave={save}
      />

      <DeleteConfirmModal
        isOpen={commitOpen}
        title="Commit CAM Categories"
        message="This updates CAM Categories everywhere in TOSP — the Budget setup wizard will offer this list, and its splits, from now on. Budgets already set up keep the splits they were built with."
        confirmLabel="Continue"
        confirmColor="#1faa59"
        error={commitError}
        onClose={() => {
          setCommitOpen(false);
          setCommitError(null);
        }}
        // Awaited so the confirmation's own spinner runs until it lands.
        //
        // A failed save has to be caught HERE. Without the catch the throw
        // skipped setCommitOpen(false), so the dialog sat open over an error
        // message it was hiding, and Cancel was the only way out of what looked
        // like a hung commit. The draft is kept either way — closing on a
        // failure would discard the very edit that didn't save.
        onConfirm={async () => {
          try {
            await onCommit(draft);
            setCommitOpen(false);
            setCommitError(null);
          } catch (err) {
            setCommitError(
              err instanceof Error
                ? err.message
                : "That didn't save. Please try again.",
            );
          }
        }}
      />

      <DeleteConfirmModal
        isOpen={!!removing}
        title="Delete CAM Category"
        message={
          removing
            ? `Remove "${removing.name}"? Budgets already set up keep it. The change takes effect when you commit.`
            : ""
        }
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          setDraft((prev) => prev.filter((c) => c.id !== removing?.id));
          setRemoving(null);
        }}
      />
    </section>
  );
}
